/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { Switch, Route, useParams } from 'react-router-dom';
import SurveyContainer from '@features/survey-container/SurveyContainer';
import { SurveyConfiguration } from '@features/survey-configuration/SurveyConfiguration';
import SurveyAssurance from '@features/survey/assurance/SurveyAssurance';
import QuestionContainer from '../../features/survey/question/QuestionContainer';
import SurveyScope from '@features/survey-scope/SurveyScope';
import SurveyDelegation from '@apps/company-tracker/components/delegation/survey/survey-delegation/SurveyDelegation';
import SurveyImportExport from './survey-import-export';
import { sidebarPath } from '@features/survey-settings-toolbar/breadcrumbPath';
import { SurveyAddAssurance } from '../../types/survey';
import { ROUTES } from '../../constants/routes';
import { SurveyOverviewWithBlockLoading } from '@features/survey/overview/SurveyOverviewWithBlockLoading';
import { AIAutoAnswerSupervisor } from '@features/ai-auto-answer-supervisor/AIAutoAnswerSupervisor';
import { generateUrl } from '../util';

interface SurveyComponentProps {
  page?: string;
  handleNavigate: (page: string, extras: { [key: string]: string }) => void;
}

const SurveyComponent = ({ page }: SurveyComponentProps) => {
  switch (page) {
    case 'configuration':
      return <SurveyConfiguration />
    case 'scope':
      return <SurveyScope />
    case 'delegation':
      return <SurveyDelegation />
    case 'import-export':
      return <SurveyImportExport />
    case 'overview':
    default:
      return <SurveyOverviewWithBlockLoading />
  }
}

export const CompanyTrackerSurveyRoute = (props: { handleAddAssurance: (survey: SurveyAddAssurance) => void }) => {

  const { path } = useRouteMatch();
  const { initiativeId, surveyId, batchId, page } = useParams<{ initiativeId: string; surveyId: string; page?: string; batchId?: string; view?: string }>();
  const history = useHistory();

  const handleNavigate = (page: string, extras: { [key: string]: string }) => {
    switch (page) {
      case 'auto-answer':
        history.push(generateUrl(ROUTES.COMPANY_TRACKER_SURVEY_AI_AUTO_ANSWER, { initiativeId, surveyId, page, batchId: extras.batchId }));
      break;
      default:
        history.push(generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page, ...extras }));
        break;
    }
  };

  return (
    <Switch>
      <Route path={`${path}/question/:questionId/:questionIndex/draft`}>
        <QuestionContainer isDraft={true} handleAddAssurance={props.handleAddAssurance} />
      </Route>

      <Route path={`${path}/question/:questionId/:questionIndex?`}>
        <QuestionContainer handleAddAssurance={props.handleAddAssurance} />
      </Route>

      <Route path={ROUTES.COMPANY_TRACKER_ASSURANCE.path}>
        <SurveyAssurance surveyId={surveyId} />
      </Route>

      <Route path={ROUTES.COMPANY_TRACKER_SURVEY_AI_AUTO_ANSWER.path}>
        <SurveyContainer handleNavigate={handleNavigate}>
          <AIAutoAnswerSupervisor batchId={batchId} initiativeId={initiativeId} surveyId={surveyId} handleNavigate={handleNavigate} />
        </SurveyContainer>
      </Route>

      <Route path={`${path}/${sidebarPath}`}>
        <SurveyContainer handleNavigate={handleNavigate}>
          <SurveyComponent page={page} handleNavigate={handleNavigate} />
        </SurveyContainer>
      </Route>
    </Switch>
  );
}
